import React, { useState, useEffect } from 'react';
import { Container, Paper, Box, TextField, Button, Typography, Alert, CircularProgress, InputAdornment, IconButton } from '@mui/material';
import { useNavigate, Link } from 'react-router-dom';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { register } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
// Import the SelectivateLogo component
import SelectivateLogo from '../components/SelectivateLogo';

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  useEffect(() => {
    // If user is already authenticated, redirect to home
    if (isAuthenticated) {
      navigate('/home');
    }
  }, [isAuthenticated, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const validateForm = () => {
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    
    // Password requirements: at least 8 chars, 1 uppercase, 1 lowercase, 1 number, 1 special char
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>]).{8,}$/;
    if (!passwordRegex.test(formData.password)) {
      setError('Password must be at least 8 characters long and include at least one uppercase letter, one lowercase letter, one number, and one special character');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);

    try {
      // Remove confirmPassword before sending to API
      const { confirmPassword, ...userData } = formData;
      
      const response = await register(userData);
      if (response.success) {
        // Redirect to login page
        navigate('/', { state: { message: 'Registration successful! Please log in.' } });
      } else {
        setError(response.message || 'Registration failed. Please try again.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      console.error('Registration error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 'calc(100vh - 80px)',
        }}
      >
        <Box
          sx={{ 
            mb: 4, 
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center', 
          }}
        >
          <Box 
            component="img" 
            src="/SELECCIÓN_logo_PNG.png"
            alt="SELECCIÓN Consulting"
            sx={{ 
              width: 180, 
              mb: 2
            }}
          />
          {/* Replace "Create Account" heading with Selectivate logo */}
          <Box sx={{ mb: 2 }}>
            <SelectivateLogo variant="banner" size="large" />
          </Box>
          <Typography variant="subtitle1" color="textSecondary" sx={{ mb: 2 }}>
            Create your account
          </Typography>
        </Box>
        
        <Paper 
          elevation={4} 
          sx={{ 
            p: 4, 
            maxWidth: 500, 
            width: '100%',
            borderRadius: 2,
            background: 'linear-gradient(to bottom, #1E1E1E, #252525)',
            border: '1px solid rgba(255, 255, 255, 0.05)',
            mb: 4
          }}
        >
          {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
          
          <form onSubmit={handleSubmit}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1, color: '#E0E0E0', fontWeight: 500 }}>
                Email Address *
              </Typography>
              <TextField
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                fullWidth
                required
                autoFocus
                variant="outlined"
                placeholder="Enter your email address"
                InputProps={{
                  sx: { borderRadius: 1 }
                }}
              />
            </Box>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1, color: '#E0E0E0', fontWeight: 500 }}>
                Full Name *
              </Typography>
              <TextField
                type="text"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                fullWidth
                required
                variant="outlined"
                placeholder="Enter your full name"
                InputProps={{
                  sx: { borderRadius: 1 }
                }}
              />
            </Box>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1, color: '#E0E0E0', fontWeight: 500 }}>
                Password *
              </Typography>
              <TextField
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleChange}
                fullWidth
                required
                variant="outlined"
                placeholder="Create a password"
                InputProps={{
                  sx: { borderRadius: 1 },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{ color: '#E0E0E0' }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Typography variant="caption" sx={{ color: '#CCCCCC', display: 'block', mt: 0.5 }}>
                Must have at least 8 characters, 1 uppercase letter, 1 lowercase letter, 1 number, and 1 special character
              </Typography>
            </Box>
            
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ mb: 1, color: '#E0E0E0', fontWeight: 500 }}>
                Confirm Password *
              </Typography>
              <TextField
                type={showConfirmPassword ? 'text' : 'password'}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                fullWidth
                required
                variant="outlined"
                placeholder="Confirm your password"
                InputProps={{
                  sx: { borderRadius: 1 },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                        sx={{ color: '#E0E0E0' }}
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              sx={{ 
                mt: 1, 
                mb: 3, 
                py: 1.2,
                fontWeight: 600,
                borderRadius: 1,
                boxShadow: '0 4px 10px rgba(245, 130, 31, 0.3)',
                '&:hover': {
                  backgroundColor: '#f06000',
                  boxShadow: '0 6px 15px rgba(245, 130, 31, 0.4)',
                }
              }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Create Account'}
            </Button>
          </form>
          
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2">
              Already have an account?{' '}
              <Link 
                to="/" 
                style={{ 
                  textDecoration: 'none', 
                  color: '#F5821F',
                  fontWeight: 500
                }}
              >
                Sign In
              </Link>
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default RegisterPage;